// ============================================================
// globale.scss.ts
// Styles regroupés par PAGE (pages/**/*.css)
// Toutes les règles provenant des fichiers de pages
// ============================================================

export const globaleStyles = `
// ============================================================
// GLOBALE.SCSS — Styles regroupés par PAGE
// Toutes les pages de l'application
// ============================================================

// ═══════════════════════════════════════════════════════════
// PAGE : ACTIVITIES (activities.component.css)
// ═══════════════════════════════════════════════════════════

.activities-container {
  height: 100%;
}

.activities-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.25rem 1rem;
}

@media (min-width: 768px) {
  .activities-wrapper {
    padding: 1.25rem 1.75rem;
  }
}

.activities-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.activities-title-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.activities-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--colorTextTitle);
  margin: 0;
}

.activities-subtitle {
  font-size: 0.875rem;
  color: var(--colorTextSubtitle);
  margin: 0;
}

.report-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}

.report-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color var(--transitionBase), transform var(--transitionFast), box-shadow var(--transitionBase);
}

.report-button mat-icon {
  font-size: 1.25rem;
  color: var(--colorWhite);
  line-height: 1;
}

.report-pdf {
  background-color: var(--colorDanger600);
  box-shadow: 0 2px 8px rgba(229, 62, 62, 0.35);
}

.report-pdf:hover {
  background-color: var(--colorDanger700);
  box-shadow: 0 4px 14px rgba(229, 62, 62, 0.45);
  transform: translateY(-1px);
}

.report-pdf:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(229, 62, 62, 0.3);
}

.report-xlsx {
  background-color: var(--colorSuccess600);
  box-shadow: 0 2px 8px rgba(56, 161, 105, 0.35);
}

.report-xlsx:hover {
  background-color: var(--colorSuccess700);
  box-shadow: 0 4px 14px rgba(56, 161, 105, 0.45);
  transform: translateY(-1px);
}

.report-xlsx:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(56, 161, 105, 0.3);
}

.activities-content {
  margin-top: 0.5rem;
}

// ═══════════════════════════════════════════════════════════
// PAGE : ACTIVITY COMPONENT (activity.component.scss)
// ═══════════════════════════════════════════════════════════

:host {
  display: block;
  font-family: var(--fontFamily);
  background: var(--colorSurfaceBg);
  min-height: 100vh;
}

.page {
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (max-width: 768px) {
  .page {
    padding: 18px 16px;
    gap: 18px;
  }
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: var(--fontSize7Xl);
  font-weight: var(--fontWeightBold);
  color: var(--colorTextPrimary);
  margin: 0;
  letter-spacing: -0.02em;
}

.page-subtitle {
  font-size: var(--fontSizeBase);
  color: var(--colorTextMuted);
  margin: 4px 0 0;
}

.toolbar {
  background: var(--colorSurface);
  border: 1.5px solid var(--colorBorder);
  border-radius: var(--radius2Xl);
  box-shadow: var(--shadowCard);
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  min-height: 64px;
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
  }
}

.toolbar-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 14px 20px;
  flex-shrink: 0;
  flex-wrap: wrap;
  border-right: 1.5px solid var(--colorBorder);
}

@media (max-width: 768px) {
  .toolbar-actions {
    padding: 14px 16px;
    border-right: none;
    border-bottom: 1.5px solid var(--colorBorder);
  }
}

.toolbar-filters {
  display: flex;
  gap: 14px;
  align-items: center;
  flex: 1;
  flex-wrap: wrap;
  padding: 14px 20px;
  min-width: 0;
}

.toolbar-filters app-select {
  flex: 1;
  min-width: 160px;
  max-width: 280px;
}

@media (max-width: 768px) {
  .toolbar-filters {
    padding: 14px 16px;
  }
  .toolbar-filters app-select {
    max-width: 100%;
  }
}

.content-card {
  background: var(--colorSurface);
  border: 1.5px solid var(--colorBorder);
  border-radius: var(--radius2Xl);
  box-shadow: var(--shadowCard);
  overflow: hidden;
}

.loader-center {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
}

.row-actions {
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  align-items: center;
}

// ═══════════════════════════════════════════════════════════
// PAGE : ADD/EDIT ACTIVITY (add.component.css, edit.component.css)
// ═══════════════════════════════════════════════════════════

.add-activity-page,
.edit-activity-page {
  min-height: 100%;
  padding-bottom: 60px;
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(6, 182, 172, 0.08) 0%, transparent 60%),
    var(--colorNeutral100);
}

.page-inner {
  max-width: 1300px;
  margin: 0 auto;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-header__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  font-weight: var(--fontWeightBold);
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--colorGray400);
}

.eyebrow-sep {
  color: var(--colorGray300);
  font-weight: 300;
  font-size: 1rem;
  line-height: 1;
}

.page-header__title {
  font-size: var(--fontSizeBase);
  font-weight: var(--fontWeightSemibold);
  color: var(--colorNeutral800);
  margin: 0;
  letter-spacing: -0.1px;
}

.stepper-wrapper {
  background: var(--colorWhite);
  border: 1px solid var(--colorBorderLight);
  border-radius: var(--radius3Xl);
  padding: 18px 28px 16px;
  box-shadow: var(--shadowSm);
}

.stepper-progress-bar {
  height: 3px;
  background: var(--colorNeutral100);
  border-radius: 99px;
  margin-bottom: 18px;
  overflow: hidden;
}

.stepper-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--btnSelectedBg), var(--colorAccentTeal));
  border-radius: 99px;
  transition: width 500ms cubic-bezier(.4, 0, .2, 1);
}

.stepper-nav {
  display: flex;
  align-items: center;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.stepper-nav::-webkit-scrollbar {
  display: none;
}

.step-connector {
  flex: 1 1 auto;
  min-width: 16px;
  height: 1.5px;
  background: var(--colorBorderLight);
  border-radius: 99px;
  transition: background 400ms ease;
  position: relative;
}

.step-connector.completed {
  background: transparent;
}

.step-connector.completed::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, var(--btnSelectedBg), var(--colorAccentTeal));
  border-radius: 99px;
  animation: connector-fill 300ms ease forwards;
}

@keyframes connector-fill {
  from { transform: scaleX(0); transform-origin: left; }
  to   { transform: scaleX(1); transform-origin: left; }
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  padding: 4px 8px;
  border-radius: var(--radiusLg);
  text-decoration: none;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 160ms ease;
  position: relative;
}

.step-item:hover:not(.disabled) {
  background: var(--colorNeutral100);
}

.step-item.disabled {
  cursor: default;
  pointer-events: none;
  opacity: 0.35;
}

.step-bubble {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: var(--fontWeightBold);
  position: relative;
  background: var(--colorNeutral100);
  border: 1.5px solid var(--colorNeutral200);
  color: var(--colorGray400);
  transition: all 300ms cubic-bezier(.4, 0, .2, 1);
}

.step-item.completed .step-bubble {
  background: var(--btnSelectedBg);
  border-color: var(--btnSelectedBg);
  color: var(--colorWhite);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--btnSelectedBg) 14%, transparent);
}

.step-item.completed .step-bubble .step-bubble__num {
  display: none;
}

.step-item.completed .step-bubble::after {
  content: '✓';
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: var(--fontWeightBold);
}

.step-item.active .step-bubble {
  background: var(--colorWhite);
  border-color: var(--btnSelectedBg);
  border-width: 2px;
  color: var(--btnSelectedBg);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--btnSelectedBg) 12%, transparent), 0 2px 8px rgba(6, 182, 172, 0.2);
}

.step-item.active .step-bubble::before {
  content: '';
  position: absolute;
  inset: -5px;
  border-radius: 50%;
  border: 1.5px solid color-mix(in srgb, var(--btnSelectedBg) 30%, transparent);
  animation: active-pulse 2.4s ease-in-out infinite;
}

@keyframes active-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0; transform: scale(1.35); }
}

.step-item.pmp .step-bubble {
  background: var(--colorVioletSurface);
  border-color: var(--colorVioletLight);
  color: var(--colorViolet);
}

.step-item.pmp.active .step-bubble {
  background: var(--colorWhite);
  border-color: var(--colorVioletVariant);
  border-width: 2px;
  box-shadow: 0 0 0 4px #8b5cf615, 0 2px 8px #8b5cf622;
}

.step-item.pmp.active .step-bubble::before {
  border-color: #8b5cf640;
}

.step-item.pmp.completed .step-bubble {
  background: var(--colorVioletVariant);
  border-color: var(--colorVioletVariant);
  box-shadow: 0 0 0 3px #8b5cf620;
}

.step-pmp-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--colorVioletVariant);
  border: 2px solid var(--colorWhite);
  animation: pmp-pulse 2s ease-in-out infinite;
}

@keyframes pmp-pulse {
  0%, 100% { box-shadow: 0 0 0 0 #8b5cf640; }
  50% { box-shadow: 0 0 0 4px transparent; }
}

.step-label {
  font-size: 0.66rem;
  font-weight: var(--fontWeightSemibold);
  color: #b0bac6;
  text-align: center;
  max-width: 80px;
  white-space: normal;
  line-height: 1.3;
  transition: color 240ms ease;
  letter-spacing: 0.01em;
}

.step-item.active .step-label {
  color: var(--btnSelectedBg);
  font-weight: var(--fontWeightBold);
}

.step-item.completed .step-label {
  color: var(--colorGray500);
}

.step-item.pmp .step-label {
  color: var(--colorVioletLight);
}

.step-item.pmp.active .step-label {
  color: var(--colorViolet);
  font-weight: var(--fontWeightBold);
}

.step-content-area {
  animation: step-enter 240ms cubic-bezier(.4, 0, .2, 1);
}

@keyframes step-enter {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.loader-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.btn-danger-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radiusMd);
  background: var(--colorDangerSurface);
  border: 1px solid var(--colorDangerRing);
  color: var(--colorDanger);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 140ms ease;
}

.btn-danger-icon:hover:not(:disabled) {
  background: var(--colorDanger);
  color: var(--colorWhite);
  border-color: var(--colorDanger);
}

.btn-danger-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-danger-icon .material-icons {
  font-size: 17px;
}

@media (max-width: 768px) {
  .page-inner {
    padding: 18px 16px;
    gap: 16px;
  }
}

@media (max-width: 640px) {
  .stepper-wrapper {
    padding: 14px 12px 12px;
  }
  .step-label {
    display: none;
  }
  .step-connector {
    min-width: 10px;
    max-width: 24px;
  }
  .step-bubble {
    width: 30px;
    height: 30px;
    font-size: 0.7rem;
  }
}

// ═══════════════════════════════════════════════════════════
// PAGE : REGROUPEMENT ACTIVITIES (add/edit regroupementactivities)
// ═══════════════════════════════════════════════════════════

.regroup-page {
  min-height: 100%;
  padding-bottom: 60px;
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(6, 182, 172, 0.08) 0%, transparent 60%),
    var(--colorNeutral100);
}

.regroup-card {
  background: var(--colorWhite);
  border: 1px solid var(--colorBorderLight);
  border-radius: var(--radius3Xl);
  padding: 24px 28px;
  box-shadow: var(--shadowSm);
}

.regroup-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.panel {
  background: var(--colorNeutral100);
  border: 1px solid var(--colorNeutral200);
  border-radius: var(--radiusXl);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  font-size: 0.875rem;
  font-weight: var(--fontWeightSemibold);
  color: var(--colorNeutral700);
  margin: 0;
}

.structures-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.structures-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.structures-row__select {
  flex: 1 1 0;
  min-width: 0;
  position: relative;
}

.structures-row__select ::ng-deep .select-list,
.structures-row__select ::ng-deep [class*="select-dropdown"],
.structures-row__select ::ng-deep [class*="select-options"],
.structures-row__select ::ng-deep [class*="options-list"] {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 1000 !important;
  max-height: 220px !important;
  overflow-y: auto !important;
  background: var(--colorWhite);
  border: 1px solid var(--colorNeutral200);
  border-radius: var(--radiusMd);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  margin-top: 4px;
}

.complementary-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: fade-in 220ms ease;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.complementary-header {
  padding-bottom: 10px;
  border-bottom: 1px solid var(--colorNeutral200);
}

.complementary-title {
  font-size: 0.9rem;
  font-weight: var(--fontWeightBold);
  color: var(--colorNeutral900);
  margin: 0;
}

@media (max-width: 768px) {
  .regroup-card {
    padding: 18px 16px;
  }
}

// ═══════════════════════════════════════════════════════════
// PAGE : STRUCTURES PROGRAMMATIQUES
// ═══════════════════════════════════════════════════════════

:host {
  font-size: var(--fontSizeBase);
}

.sp-top-panel {
  display: grid;
  grid-template-columns: 320px auto 1fr;
  align-items: stretch;
  background: var(--colorSurfaceInput);
  border: 1px solid var(--colorBorderCard);
  border-radius: var(--radius3Xl);
  overflow: hidden;
  box-shadow: var(--shadowCard2);
}

.sp-divider-v {
  width: 1px;
  background: linear-gradient(to bottom, transparent 0%, var(--colorBorderCard) 20%, var(--colorBorderCard) 80%, transparent 100%);
  margin: 20px 0;
}

.sp-form-card {
  display: flex;
  flex-direction: column;
  background: var(--colorWhite);
  height: 100%;
}

.sp-form-card__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--colorBorderDivider);
  flex-shrink: 0;
}

.sp-form-card__icon {
  width: 38px;
  height: 38px;
  border-radius: var(--radiusLg);
  background: linear-gradient(135deg, var(--colorLevel0) 0%, var(--colorPrimary600) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(11, 102, 209, 0.25);
}

.sp-form-card__icon mat-icon {
  color: var(--colorWhite);
  font-size: 19px;
  width: 19px;
  height: 19px;
  line-height: 19px;
}

.sp-form-card__title {
  font-size: 14px;
  font-weight: var(--fontWeightBold);
  color: var(--colorNeutral950);
  margin: 0 0 2px;
  letter-spacing: -0.2px;
}

.sp-form-card__subtitle {
  font-size: 11.5px;
  color: var(--colorTextLight);
  margin: 0;
}

.sp-form-card__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  padding: 18px 24px;
}

.sp-form-card__footer {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px 18px;
  margin-top: auto;
  border-top: 1px solid var(--colorBorderDivider);
}

.sp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sp-field__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: var(--fontWeightBold);
  color: #0f1724;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.sp-field__label-icon {
  font-size: 13px !important;
  width: 13px !important;
  height: 13px !important;
  line-height: 13px !important;
}

.sp-field__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.sp-field__input {
  width: 100%;
  box-sizing: border-box;
  background: var(--colorSurfaceInput);
  border: 1.5px solid var(--colorBorderInput2);
  border-radius: var(--radiusLg);
  padding: 9px 13px;
  font-size: 13px;
  color: var(--colorNeutral950);
  outline: none;
  transition: border-color var(--transitionBase), box-shadow var(--transitionBase), background var(--transitionBase);
}

.sp-field__input::placeholder {
  color: #a0aab8;
}

.sp-field__input:hover {
  border-color: #c8d0df;
  background: var(--colorWhite);
}

.sp-field__input:focus {
  border-color: var(--colorLevel0);
  background: var(--colorWhite);
  box-shadow: var(--shadowInputFocus);
}

.sp-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: var(--fontWeightSemibold);
}

.sp-badge--type {
  background: linear-gradient(135deg, rgba(11, 102, 209, 0.08) 0%, rgba(124, 58, 237, 0.06) 100%);
  color: var(--colorPrimary600);
  border: 1px solid rgba(11, 102, 209, 0.18);
}

.sp-table-wrapper {
  margin-top: 18px;
  border-radius: var(--radius3Xl);
  overflow: visible;
  border: 1px solid var(--colorBorderCard);
  box-shadow: var(--shadowCard2);
  background: var(--colorWhite);
}

// ═══════════════════════════════════════════════════════════
// PAGE : BUDGET (budget.component.css)
// ═══════════════════════════════════════════════════════════

.budgets-container {
  height: 100%;
}

.budgets-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1rem;
}

@media (min-width: 768px) {
  .budgets-wrapper {
    padding: 1.25rem 1.75rem;
  }
}

.budgets-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.budgets-title-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
}

@media (min-width: 768px) {
  .budgets-title-row {
    justify-content: space-between;
  }
}

.budgets-title {
  display: none;
  font-style: bold;
  font-size: var(--fontSize10Xl);
  font-weight: var(--fontWeightBold);
  color: var(--colorTextTitle);
  margin: 0;
}

@media (min-width: 768px) {
  .budgets-title {
    display: block;
  }
}

.budgets-subtitle {
  font-size: 0.975rem;
  color: var(--colorTextTitle);
  margin: 0;
}

.budgets-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.budgets-actions {
  display: flex;
  flex-direction: row;
  margin-bottom: 0.625rem;
}

.budgets-table {
  width: 100%;
}

// ═══════════════════════════════════════════════════════════
// PAGE : DASHBOARD (dashboard.component.css)
// ═══════════════════════════════════════════════════════════

.amazing-dashboard {
  --card-bg: rgba(255, 255, 255, 0.02);
  --card-border: rgba(255, 255, 255, 0.04);
  --muted: rgba(255, 255, 255, 0.72);
  --muted-2: rgba(255, 255, 255, 0.55);
  --success: var(--colorSuccess500);
  --warning: var(--colorWarning500);
   --danger: var(--colorDanger);
  --radius: var(--radiusLg);
  color: var(--appTextColor);
  background: var(--colorNeutral900);
  padding: 1rem;
  box-sizing: border-box;
  font-family: var(--fontFamilyAlt);
}

.amazing-dashboard .header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.amazing-dashboard .header-actions {
  display: flex;
  gap: 0.5rem;
}

.header-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.page-title {
  font-size: var(--fontSize7Xl);
  margin: 0;
  color: var(--appTextColor);
}

.page-subtitle {
  margin: 0.25rem 0 0;
  color: var(--appTextColor);
}

.btn {
  background: transparent;
  border: 1px solid var(--card-border);
  color: var(--muted);
  padding: 8px 12px;
  border-radius: var(--radiusMd);
  cursor: pointer;
  font-weight: var(--fontWeightSemibold);
}

.btn:hover {
  background: rgba(255, 255, 255, 0.02);
}

.btn:active {
  transform: translateY(1px);
}

.stats-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(1, 1fr);
  margin-bottom: 1rem;
}

@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

.middle-grid {
  display: grid;
  gap: 1rem;
  margin-bottom: 1rem;
  grid-template-columns: 1fr;
}

@media (min-width: 960px) {
  .middle-grid {
    grid-template-columns: 2fr 1fr;
  }
}

.bottom-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
}

@media (min-width: 960px) {
  .bottom-grid {
    grid-template-columns: 2fr 1fr;
  }
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.02) inset;
}

.card-body {
  padding: 1rem;
}

.card.kpi {
  display: flex;
  align-items: center;
  justify-content: center;
}

.card.kpi .card-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.kpi-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.kpi-title {
  font-size: 0.9rem;
  color: var(--muted);
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: var(--fontWeightBold);
  color: var(--appTextColor);
  margin: 0;
}

.kpi-delta {
  font-size: 0.85rem;
  color: var(--muted-2);
}

.kpi-delta.positive {
  color: var(--success);
}

.kpi-delta.negative {
  color: var(--danger);
}

.kpi-icon mat-icon {
  font-size: 32px;
  color: var(--colorCyan);
}

.kpi-icon {
  width: 56px;
  height: 56px;
  min-width: 56px;
  border-radius: var(--radiusXl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) inset;
}

.tx-positive {
  color: var(--colorSuccess600);
  font-weight: var(--fontWeightSemibold);
}

.tx-negative {
  color: var(--colorDanger500);
  font-weight: var(--fontWeightSemibold);
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: var(--radiusFull);
  font-size: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  color: var(--muted);
}

.badge-green {
  background: rgba(16, 185, 129, 0.12);
  color: var(--success);
}

.badge-yellow {
  background: rgba(245, 158, 11, 0.08);
  color: var(--warning);
}

.badge-red {
  background: rgba(251, 113, 133, 0.08);
  color: var(--danger);
}

// ═══════════════════════════════════════════════════════════
// PAGE : ADMINISTRATION (add/edit agent, utilisateurs)
// ═══════════════════════════════════════════════════════════

:host {
  --c-primary: var(--colorPrimary500);
  --c-primary-dark: var(--colorPrimary600);
  --c-primary-light: var(--colorPrimaryLight2);
  --c-primary-ring: var(--colorPrimaryRing);
  --c-danger: var(--colorDanger);
  --c-danger-light: var(--colorDangerSurface);
  --c-danger-ring: var(--colorDangerRing);
  --c-warning: var(--colorWarning);
  --c-warning-light: var(--colorWarningSurface);
  --c-text: var(--colorNeutral800);
  --c-text-muted: var(--colorGray500);
  --c-border: var(--colorBorder);
  --c-surface: var(--colorWhite);
  --c-bg: var(--colorBgPage);
  --r-md: var(--radiusLg);
  --r-lg: var(--radius2Xl);
  --r-xl: var(--radius3Xl);
  --shadow-card: var(--shadowCard3);
  --transition: var(--transitionBase);
  display: block;
}

.adm-page {
  background: var(--c-bg);
  min-height: 100%;
  padding: 2rem 1.5rem 4rem;
}

.adm-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.adm-page__header-left {
  display: flex;
  align-items: center;
  gap: .875rem;
}

.adm-page__icon {
  width: 44px;
  height: 44px;
  border-radius: var(--r-lg);
  background: var(--c-primary-light);
  color: var(--c-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px var(--c-primary-ring);
}

.adm-page__title {
  font-size: 1.25rem;
  font-weight: var(--fontWeightBold);
  color: var(--c-text);
  margin: 0 0 3px;
  letter-spacing: -.01em;
}

.adm-page__desc {
  font-size: .8125rem;
  color: var(--c-text-muted);
  margin: 0;
}

.adm-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  animation: cardIn .28s ease both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.adm-card__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .75rem 1.25rem;
  border-bottom: 1px solid var(--c-border);
  background: var(--colorBgAlt);
}

.adm-card__toolbar-left {
  display: flex;
  align-items: center;
  gap: .5rem;
}

.adm-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 20px;
  background: var(--c-primary);
  color: var(--colorWhite);
  font-size: .72rem;
  font-weight: var(--fontWeightBold);
  line-height: 1;
}

.adm-card__count {
  font-size: .8125rem;
  color: var(--c-text-muted);
  font-weight: 500;
}

.adm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .625rem;
  padding: 3.5rem 1.5rem;
  text-align: center;
}

.adm-empty__icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--c-primary-light);
  color: var(--c-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: .375rem;
}

.adm-empty__title {
  font-size: .9375rem;
  font-weight: var(--fontWeightSemibold);
  color: var(--c-text);
  margin: 0;
}

.adm-empty__hint {
  font-size: .8125rem;
  color: var(--c-text-muted);
  margin: 0;
  max-width: 320px;
}

.adm-row-actions {
  display: flex;
  gap: .5rem;
  align-items: center;
}

.adm-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.adm-edit-banner {
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: .625rem .875rem;
  background: var(--c-warning-light);
  border: 1px solid var(--colorWarningRing);
  border-radius: var(--r-md);
  font-size: .8125rem;
  color: var(--c-warning);
  font-weight: 500;
}

.adm-edit-banner strong {
  font-weight: var(--fontWeightBold);
}

.adm-form__field {
  display: flex;
  flex-direction: column;
  gap: .375rem;
}

.adm-form__error {
  display: flex;
  align-items: center;
  gap: .3rem;
  font-size: .8125rem;
  font-weight: 500;
  color: var(--c-danger);
}

.adm-form__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .75rem;
  padding-top: .625rem;
  border-top: 1px solid var(--c-border);
}

.adm-form-page {
  background: var(--c-bg);
  min-height: 100%;
  padding: 2rem 1.5rem 4rem;
}

.adm-form-page__inner {
  max-width: 860px;
  margin: 0 auto;
}

.adm-form-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  animation: cardIn .28s ease both;
}

.adm-form-card__header {
  display: flex;
  align-items: center;
  gap: .875rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--c-border);
  background: var(--colorBgAlt);
}

.adm-form-card__header-icon {
  width: 40px;
  height: 40px;
  border-radius: var(--r-lg);
  background: var(--c-primary-light);
  color: var(--c-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px var(--c-primary-ring);
}

.adm-form-card__title {
  font-size: 1rem;
  font-weight: var(--fontWeightBold);
  color: var(--c-text);
  margin: 0 0 2px;
}

.adm-form-card__subtitle {
  font-size: .8125rem;
  color: var(--c-text-muted);
  margin: 0;
}

.adm-form-card__body {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.adm-form__row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.adm-form__section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--c-border);
}

.adm-form__section-title {
  font-size: .75rem;
  font-weight: var(--fontWeightBold);
  color: var(--c-text-muted);
  text-transform: uppercase;
  letter-spacing: .06em;
  margin: 0;
}

@media (max-width: 640px) {
  .adm-form__row {
    grid-template-columns: 1fr;
  }
  .adm-page__header,
  .adm-form__actions {
    flex-direction: column;
    align-items: flex-start;
  }
  .adm-form__actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
}

// ═══════════════════════════════════════════════════════════
// PAGE : EXPRESSION DE BESOIN
// ═══════════════════════════════════════════════════════════

.eb-container {
  height: 100%;
}

.eb-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.75rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.eb-header {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--colorBorderLight);
}

.eb-header-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.eb-title {
  font-size: 1.375rem;
  font-weight: var(--fontWeightBold);
  color: var(--colorTextTitle);
  margin: 0;
  letter-spacing: -0.3px;
}

.eb-subtitle {
  font-size: 0.875rem;
  color: var(--colorTextSubtitle);
  margin: 0;
}

.eb-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 0;
}

.eb-table {
  width: 100%;
}

.eb-row-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.375rem;
}

// ═══════════════════════════════════════════════════════════
// PAGE : MARCHES (marches.component.css)
// ═══════════════════════════════════════════════════════════

.marches-container {
  height: 100%;
}

.marches-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1.25rem 1rem;
}

@media (min-width: 768px) {
  .marches-wrapper {
    padding: 1.25rem 1.75rem;
  }
}

.marches-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.marches-title-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.marches-title {
  font-size: var(--fontSize10Xl);
  font-style: bold;
  font-weight: var(--fontWeightBold);
  color: var(--colorTextTitle);
  margin: 0;
}

.marches-subtitle {
  font-size: 0.975rem;
  color: var(--colorTextTitle);
  margin: 0;
}

.marches-content {
  margin-top: 0.5rem;
}

// ═══════════════════════════════════════════════════════════
// PAGE : PARAMÈTRES (parametres.component.css)
// ═══════════════════════════════════════════════════════════

.parametres-container {
  height: 100%;
  padding: 1.25rem 1.75rem;
}

@media (min-width: 768px) {
  .parametres-container {
    padding: 1.25rem 1.75rem;
  }
}

// ═══════════════════════════════════════════════════════════
// PAGE : INFO SYSTÈME (info-systeme.component.scss)
// ═══════════════════════════════════════════════════════════

.st-form {
  display: flex;
  flex-direction: column;
  gap: 22px;
  max-width: 560px;
  padding: 28px;
  background: var(--colorWhite);
  border: 1.5px solid #ececf3;
  border-radius: var(--radius2Xl);
  box-shadow: var(--shadowInput);
  font-family: var(--fontFamily);
}

.logo-upload {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.logo-upload__label {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--colorBlack);
}

.logo-upload__dropzone {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1.5px dashed #b5b5cc;
  border-radius: var(--radiusXl);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  background: var(--colorSurface);
  transition: border-color var(--transitionBase), background var(--transitionBase);
}

.logo-upload__dropzone:hover {
  border-color: var(--colorPrimary);
  background: var(--colorSurfaceAlt2);
}

.logo-upload__preview {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.logo-upload__placeholder {
  font-size: 0.72rem;
  color: var(--colorTextMuted);
  text-align: center;
  padding: 0 8px;
}

.logo-upload__overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-upload__spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(91, 79, 255, 0.25);
  border-top-color: var(--colorPrimary);
  border-radius: 50%;
  animation: logo-spin 0.6s linear infinite;
}

.logo-upload__input {
  display: none;
}

.logo-upload__btn {
  align-self: flex-start;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--colorPrimary);
  background: transparent;
  border: 1.5px solid var(--colorPrimary);
  border-radius: var(--radiusMd);
  cursor: pointer;
  transition: background var(--transitionBase);
}

.logo-upload__btn:hover:not(:disabled) {
  background: var(--colorPrimarySoft);
}

.logo-upload__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.logo-upload__error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 5px;
  background: var(--colorWarningBg);
  border: 1px solid var(--colorWarningRing);
  border-radius: var(--radiusMd);
  font-size: 0.78rem;
  color: var(--colorWarning700);
  margin: 0;
  width: fit-content;
}

.logo-upload__error-icon {
  width: 16px;
  height: 16px;
  color: var(--colorWarning500);
  flex-shrink: 0;
}

@keyframes logo-spin {
  to { transform: rotate(360deg); }
}

// ═══════════════════════════════════════════════════════════
// PAGE : THEME (theme.component.css)
// ═══════════════════════════════════════════════════════════

:host {
  display: block;
}

.p-4 { padding: 1rem; }
.mb-4 { margin-bottom: 1rem; }
.ml-3 { margin-left: 0.75rem; }
.mt-6 { margin-top: 1.5rem; }
.text-lg { font-size: 1.125rem; }
.font-bold { font-weight: var(--fontWeightBold); }
.bg-blue-600 { background: var(--colorPrimary500); }
.text-white { color: var(--appTextColor); }
.rounded { border-radius: 0.375rem; }
.border { border: 1px solid var(--colorGray300); }
.h-10 { height: 2.5rem; }
.w-16 { width: 4rem; }
.inline-block { display: inline-block; }
.w-6 { width: 1.5rem; }
.h-6 { height: 1.5rem; }
.align-middle { vertical-align: middle; }

.tabs {
  display: flex;
  gap: 0.5rem;
}

.tab-btn {
  padding: 0.4rem 0.75rem;
  border-radius: 0.375rem;
  background: var(--colorGray100);
  border: 1px solid var(--colorGray200);
}

.tab-btn.active {
  background: var(--colorPrimary500);
  color: var(--appTextColor);
}

:host ::ng-deep .p-tabview .p-tabview-nav li a {
  padding: 0.6rem 1rem;
  border-radius: var(--radiusMd);
  color: var(--colorNeutral700);
  transition: all 160ms ease;
  font-weight: var(--fontWeightBold);
  font-size: 1.0625rem;
}

:host ::ng-deep .p-tabview .p-tabview-nav li a:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

:host ::ng-deep .p-tabview .p-tabview-nav li {
  margin-right: 5px;
}

:host ::ng-deep .p-tabview .p-tabview-nav li.p-tabview-selected a {
  background: linear-gradient(180deg, var(--colorDanger500), var(--colorDanger700));
  color: var(--appTextColor) !important;
  box-shadow: 0 10px 30px rgba(189, 39, 35, 0.18);
  transform: translateY(-3px);
  border: none !important;
}

// ═══════════════════════════════════════════════════════════
// PAGE : INSCRIPTION & LOGIN
// ═══════════════════════════════════════════════════════════

.button_primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;